import { useMemo, useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  MultiSelect,
  NavLink,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Checkbox,
} from '@mantine/core';
import { Link, useNavigate, useParams } from 'react-router';
import type { z } from 'zod';
import type { LookupItemDetail, LookupListeKodu } from '@isg/shared-types';
import {
  GOREVLENDIRME_NESNE_EKSENLERI,
  SERT_KURAL_ETIKETI,
  SOZLUK_LISTE_ENVANTERI,
  SURE_SINIFLARI,
  lookupListeKoduSchema,
  readBakanlikKodu,
  readGecerlilikAy,
  readGorevlendirmeKurali,
  readMuayenePeriyodu,
  readSureSinifi,
  readUyariEsigiGun,
  readEvrakTipiKurali,
  EVRAK_EKSENLERI,
  EVRAK_EKSENI_ETIKETI,
  sozlukEklenebilirMi,
  readAcilDurumEkipmaniMi,
} from '@isg/validation';
import type {
  GorevlendirmeNesneEkseni,
  SozlukKuralEditoru,
  SozlukModulu,
  SureSinifi,
  ZorunluSayi,
  digerKuyruguSatirSchema,
} from '@isg/validation';
import {
  useDigerKalemEkle,
  useDigerKuyrugu,
  useLookup,
  useLookupKalemEkle,
  useLookupKalemGuncelle,
  useLookupKalemSil,
  useLookupKuralYaz,
  useOlayOptions,
  useKapilar,
} from '../api/queries';
import { SOZLUK_LISTE_ETIKET, SOZLUK_MODUL_ETIKET, SOZLUK_SINIF_ETIKET } from '../i18n/tr';
import { Rozet, SatirEylemleri, SolukSatir, KapiSebebi } from '../sozlesme';
import { ANLAM } from '../theme';
import { BosListeNotu, QueryStatus } from './parts';

/**
 * **GENEL SÖZLÜK YÖNETİMİ EKRANI** (Tur 5½ / A1-A5 — Kapsam §19/16).
 *
 * > Tur 5 keşfi: *31 liste, 0 ekleme yüzeyi.* `POST lookup/:listeKodu` ucu
 * > F1·S2'den beri vardı ve **hiçbir ekran çağırmıyordu** — bu ekran ilk
 * > çağıranıdır. Yeni uç YOK, yeni şapka kararı YOK.
 *
 * Solda liste kodları (modül gruplu — envanter `SOZLUK_LISTE_ENVANTERI`),
 * sağda seçili listenin kalemleri. Her listede üç iş: **kalem ekleme**
 * (soluk satır + üstte tetikleyici — §20.3b/P1-a şerhi, E1) · **gizle /
 * göster** (silme yok — §19/9; müşteri kalemi ⋮ altından silinir) · **kural
 * düzenleme, kalem tipine göre** (4½ B4 deseni — editör envanterden okunur).
 *
 * ⚠️ **DÖRT ÖZEL EKRAN BURAYA KATLANDI** (A3): *"Diğer" kuyruğu* ·
 * *Görevlendirme türleri* · *Eğitim türleri* · *Eğitim konuları / Muayene
 * türleri*. İkinci gövde bırakılmadı; eski adresler yönlendirir. Kuyruk
 * dönüştürmesinin kapısı **aynı uç** (`incidents/diger-kuyrugu/kalem` —
 * `olay_inceleme` / `risk_degerlendirme`); düz ekleme `POST lookup`.
 *
 * ⚠️ **SABİT LİSTEDE EKLEME YÜZEYİ ÇİZİLMEZ** (0b) — sunucu da 422 der; ekran
 * çalışamayacak kontrolü göstermez (§2.40).
 * ⚠️ **A2 asimetrisi:** nüsha gizlenebilir; adı kilitli; kuralı **sert**
 * (mevzuat sınırlı) ise gevşetilemez — hangi listenin sert kural taşıdığı
 * `SERT_KURAL_ETIKETI`nden okunur ve rozetle söylenir; ret sunucudan 422.
 * ⚠️ **BİLİNMEYEN KURAL ANAHTARLARI KORUNUR** (B4 dersi) — her editör mevcut
 * `kurallar` üstüne yazar.
 * ⚠️ **M1:** ekranda kavram açıklaması yok.
 */

type KuyrukSatiri = z.output<typeof digerKuyruguSatirSchema>;
type Kalem = LookupItemDetail;

const MODUL_SIRASI: readonly SozlukModulu[] = [
  'calisan',
  'egitim',
  'saglik',
  'ekipman',
  'izin',
  'olay',
  'aksiyon',
  'risk',
  'yuklenici',
  'saha_turu',
  'kkd',
];

const KAYNAK_YOLU: Record<string, (id: string) => string> = {
  olay: (id) => `/olaylar/${id}`,
  risk: (id) => `/risk/${id}`,
};
const KAYNAK_ETIKET: Record<string, string> = { olay: 'Olay', risk: 'Risk' };

export function SozlukYonetimiScreen(): React.JSX.Element {
  const { listeKodu: listeKoduRaw } = useParams<{ listeKodu?: string }>();
  const cozum = listeKoduRaw ? lookupListeKoduSchema.safeParse(listeKoduRaw) : null;
  const listeKodu: LookupListeKodu | null = cozum?.success ? cozum.data : null;
  // P13-b (madde 225): iki kapı SUNUCUDAN (`GET kapilar`) — kalem yazma `SOZLUK_YAZMA` ·
  // nüsha gizleme yalnız holding yön. (§19/9); ekran şapka dizgesi yazmaz.
  const kapilar = useKapilar();
  const yazabilir = kapilar.data?.sozlukKapisi.yazabilir ?? false;
  const holding = kapilar.data?.sozlukNushaKapisi.yazabilir ?? false;

  const gruplar = useMemo(() => {
    const kodlar = Object.keys(SOZLUK_LISTE_ENVANTERI) as LookupListeKodu[];
    return MODUL_SIRASI.map((modul) => ({
      modul,
      kodlar: kodlar
        // Sökülmüş liste (T2·0) çizilmez — ölü listeye gezinti verilmez.
        .filter((k) => SOZLUK_LISTE_ENVANTERI[k].modul === modul && k !== 'olay_kapanis_gerekcesi')
        .sort((a, b) => SOZLUK_LISTE_ETIKET[a].localeCompare(SOZLUK_LISTE_ETIKET[b], 'tr')),
    })).filter((g) => g.kodlar.length > 0);
  }, []);

  return (
    <Stack>
      <Title order={3}>Sözlükler</Title>
      <Grid>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card withBorder padding="xs" data-testid="sozluk-liste-gezinti">
            <NavLink
              component={Link}
              to="/sozluk-bakim"
              label="“Diğer” kuyruğu"
              active={listeKodu === null}
              data-testid="sozluk-gezinti-kuyruk"
            />
            {gruplar.map((grup) => (
              <NavLink
                key={grup.modul}
                label={SOZLUK_MODUL_ETIKET[grup.modul]}
                defaultOpened={
                  listeKodu !== null && SOZLUK_LISTE_ENVANTERI[listeKodu].modul === grup.modul
                }
                childrenOffset={12}
              >
                {grup.kodlar.map((kod) => (
                  <NavLink
                    key={kod}
                    component={Link}
                    to={`/sozluk-bakim/${kod}`}
                    label={SOZLUK_LISTE_ETIKET[kod]}
                    active={listeKodu === kod}
                    data-testid={`sozluk-gezinti-${kod}`}
                  />
                ))}
              </NavLink>
            ))}
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9 }}>
          {listeKoduRaw && listeKodu === null && (
            <BosListeNotu mesaj="Bilinmeyen sözlük listesi." />
          )}
          {listeKodu === null && !listeKoduRaw && <DigerKuyruguBolumu listeKodu={null} />}
          {listeKodu !== null && kapilar.data && (
            <KapiSebebi
              durum={kapilar.data.sozlukKapisi}
              nesne="Sözlük kalemini"
              fiil="yazar"
              testId="sozluk-yazamama-sebebi"
            />
          )}
          {listeKodu !== null && kapilar.data && (
            <KapiSebebi
              durum={kapilar.data.sozlukNushaKapisi}
              nesne="Kütüphane nüshasını"
              fiil="gizler"
              testId="sozluk-nusha-yazamama-sebebi"
            />
          )}
          {listeKodu !== null && (
            <ListeGovdesi
              key={listeKodu}
              listeKodu={listeKodu}
              yazabilir={yazabilir}
              holding={holding}
            />
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LİSTE GÖVDESİ — kalemler · ekleme · gizle/göster · kural editörü
// ═══════════════════════════════════════════════════════════════════════════

function ListeGovdesi({
  listeKodu,
  yazabilir,
  holding,
}: {
  listeKodu: LookupListeKodu;
  yazabilir: boolean;
  holding: boolean;
}): React.JSX.Element {
  const tanim = SOZLUK_LISTE_ENVANTERI[listeKodu];
  const kalemler = useLookup(listeKodu);
  const ekle = useLookupKalemEkle(listeKodu);
  const guncelle = useLookupKalemGuncelle(listeKodu);
  const sil = useLookupKalemSil(listeKodu);
  const kuralYaz = useLookupKuralYaz(listeKodu);
  // Şart kalemleri (görevlendirme editörü) — iki listeden (§25/4).
  const editorGorevlendirme = tanim.editor === 'gorevlendirme';
  const yetkinlikler = useLookup('yetkinlik_tipi', { enabled: editorGorevlendirme });
  const egitimTurleri = useLookup('egitim_turu', { enabled: editorGorevlendirme });
  // Ana kalem / grup bağı için kaynak listeler.
  const grupIster = tanim.eklemeSarti === 'grup';
  const gruplar = useLookup('kok_sebep_grubu', { enabled: grupIster });

  const [ekleAcik, setEkleAcik] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const eklenebilir = yazabilir && sozlukEklenebilirMi(listeKodu);
  const sertKural = SERT_KURAL_ETIKETI[listeKodu];

  const sartSecenekleri = [
    ...(yetkinlikler.data ?? []).filter((k) => !k.gizli).map((k) => ({ value: k.id, label: k.ad })),
    ...(egitimTurleri.data ?? [])
      .filter((k) => !k.gizli)
      .map((k) => ({ value: k.id, label: k.ad })),
  ];
  const sartAdi = new Map(sartSecenekleri.map((s) => [s.value, s.label]));

  const anaKalemler = (kalemler.data ?? []).filter((k) => k.kutuphaneRef !== null && !k.gizli);
  const grupSecenekleri = (gruplar.data ?? [])
    .filter((g) => g.kutuphaneRef !== null)
    .map((g) => ({ value: g.kutuphaneRef as string, label: g.ad }));

  const KOLON = 4 + (tanim.editor ? 1 : 0);

  return (
    <Stack>
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={4} data-testid="sozluk-liste-baslik">
            {SOZLUK_LISTE_ETIKET[listeKodu]}
          </Title>
          <Group gap="xs">
            <Rozet anlam="bilgi" testId="sozluk-liste-sinif">
              {SOZLUK_SINIF_ETIKET[tanim.sinif]}
            </Rozet>
            {sertKural && (
              <Rozet anlam="dikkat" testId="sozluk-sert-kural">
                {sertKural}
              </Rozet>
            )}
          </Group>
        </Stack>
        {/* E1 — sayfalanmayan liste de olsa üstte tetikleyici: soluk satır
            listenin BAŞINDA açılır (§20.3b/P1-a şerhi). */}
        {eklenebilir && (
          <Button
            size="xs"
            variant="default"
            data-testid="sozluk-kalem-ekle-ac"
            onClick={() => {
              setHata(null);
              setEkleAcik(true);
            }}
            disabled={ekleAcik}
          >
            Kalem ekle
          </Button>
        )}
      </Group>
      <QueryStatus queries={[kalemler]} />
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="sozluk-kural-hata">
          {hata}
        </Alert>
      )}
      {kalemler.data && (
        <Table striped data-testid="sozluk-kalem-listesi">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Kalem</Table.Th>
              <Table.Th w={120}>Kaynak</Table.Th>
              <Table.Th w={90}>Durum</Table.Th>
              {tanim.editor && <Table.Th>{editorBasligi(tanim.editor)}</Table.Th>}
              <Table.Th w={160} aria-label="Satır eylemi" />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ekleAcik && (
              <KalemEkleSatiri
                listeKodu={listeKodu}
                kolonSayisi={KOLON}
                anaKalemler={anaKalemler}
                grupSecenekleri={grupSecenekleri}
                kaydediliyor={ekle.isPending}
                onVazgec={() => setEkleAcik(false)}
                onKaydet={(girdi) =>
                  ekle.mutate(girdi, {
                    onSuccess: () => {
                      setHata(null);
                      setEkleAcik(false);
                    },
                    onError: (e) => setHata(e.message),
                  })
                }
              />
            )}
            {kalemler.data.length === 0 && !ekleAcik && (
              <Table.Tr>
                <Table.Td colSpan={KOLON + 1}>
                  <Text size="sm" c="dimmed">
                    Bu listede kalem yok.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {kalemler.data.map((kalem) =>
              duzenlenen === kalem.id ? (
                <KalemDuzenleSatiri
                  key={kalem.id}
                  kalem={kalem}
                  editor={tanim.editor}
                  sartSecenekleri={sartSecenekleri}
                  kaydediliyor={kuralYaz.isPending || guncelle.isPending}
                  onVazgec={() => setDuzenlenen(null)}
                  onKaydet={(kurallar, ad) => {
                    setHata(null);
                    const bitir = {
                      onSuccess: () => setDuzenlenen(null),
                      onError: (e: Error) => setHata(e.message),
                    };
                    if (kurallar !== null) {
                      kuralYaz.mutate(
                        { id: kalem.id, kurallar },
                        {
                          onSuccess: () => {
                            if (ad !== null && ad !== kalem.ad) {
                              guncelle.mutate({ id: kalem.id, ad }, bitir);
                            } else {
                              setDuzenlenen(null);
                            }
                          },
                          onError: (e) => setHata(e.message),
                        },
                      );
                    } else if (ad !== null && ad !== kalem.ad) {
                      guncelle.mutate({ id: kalem.id, ad }, bitir);
                    } else {
                      setDuzenlenen(null);
                    }
                  }}
                />
              ) : (
                <Table.Tr
                  key={kalem.id}
                  data-testid="sozluk-kalem-satiri"
                  style={kalem.gizli ? { opacity: 0.55 } : undefined}
                >
                  <Table.Td>{kalem.ad}</Table.Td>
                  <Table.Td>
                    <Rozet anlam="bilgi" testId="sozluk-kalem-kaynak">
                      {kalem.kutuphaneRef !== null ? 'Kütüphane' : 'Müşteri'}
                    </Rozet>
                  </Table.Td>
                  <Table.Td>
                    {kalem.gizli ? (
                      <Rozet anlam="dikkat" testId="sozluk-kalem-gizli">
                        Gizli
                      </Rozet>
                    ) : (
                      <Text size="xs" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                  {tanim.editor && (
                    <Table.Td>
                      <KuralOzeti editor={tanim.editor} kalem={kalem} sartAdi={sartAdi} />
                    </Table.Td>
                  )}
                  <Table.Td>
                    {yazabilir && (
                      <SatirEylemleri
                        testId={`sozluk-kalem-eylemleri-${kalem.id}`}
                        birincil={
                          tanim.editor || kalem.kutuphaneRef === null
                            ? {
                                etiket: 'Düzenle',
                                testId: 'sozluk-kalem-duzenle',
                                onClick: () => {
                                  setHata(null);
                                  setDuzenlenen(kalem.id);
                                },
                              }
                            : undefined
                        }
                        digerleri={[
                          {
                            etiket: kalem.gizli ? 'Göster' : 'Gizle',
                            // Nüshayı gizleme holding yöneticisinde kalır
                            // (§19/10); uzmana çalışamayacak kontrol gösterilmez.
                            gizli: kalem.kutuphaneRef !== null && !holding,
                            testId: 'sozluk-kalem-gizle',
                            onClick: () =>
                              guncelle.mutate(
                                { id: kalem.id, gizli: !kalem.gizli },
                                { onError: (e) => setHata(e.message) },
                              ),
                          },
                          {
                            etiket: 'Sil',
                            tehlikeli: true,
                            // Nüsha silinemez (§19/9) — seçenek çizilmez.
                            gizli: kalem.kutuphaneRef !== null,
                            testId: 'sozluk-kalem-sil',
                            onClick: () =>
                              sil.mutate({ id: kalem.id }, { onError: (e) => setHata(e.message) }),
                          },
                        ]}
                      />
                    )}
                  </Table.Td>
                </Table.Tr>
              ),
            )}
          </Table.Tbody>
        </Table>
      )}
      {tanim.digerKuyrugu && <DigerKuyruguBolumu listeKodu={listeKodu} />}
    </Stack>
  );
}

function editorBasligi(editor: NonNullable<SozlukKuralEditoru>): string {
  switch (editor) {
    case 'sure_sinifi':
      return 'Süre türü · yenileme periyodu (ay — az / tehlikeli / çok)';
    case 'bakanlik_kodu':
      return 'Bakanlık kodu (İBYS)';
    case 'muayene':
      return 'Bakanlık kodu · periyot (ay — az / tehlikeli / çok)';
    case 'uyari_esigi':
      return 'Uyarı eşiği (gün)';
    case 'gorevlendirme':
      return 'Nesne ekseni · gerekli belgeler · sağlık · zorunlu sayı';
    case 'evrak':
      return 'Eksen · vade takibi · zorunlu · sağlık · uyarı eşiği (gün)';
  }
}

// ── Kalem ekleme (soluk satır) ──────────────────────────────────────────────

function KalemEkleSatiri({
  listeKodu,
  kolonSayisi,
  anaKalemler,
  grupSecenekleri,
  kaydediliyor,
  onKaydet,
  onVazgec,
}: {
  listeKodu: LookupListeKodu;
  kolonSayisi: number;
  anaKalemler: Kalem[];
  grupSecenekleri: { value: string; label: string }[];
  kaydediliyor: boolean;
  onKaydet: (girdi: {
    ad: string;
    anaKalemId?: string | null;
    kurallar?: Record<string, unknown> | null;
  }) => void;
  onVazgec: () => void;
}): React.JSX.Element {
  const tanim = SOZLUK_LISTE_ENVANTERI[listeKodu];
  const [ad, setAd] = useState('');
  const [anaKalemId, setAnaKalemId] = useState<string | null>(null);
  const [grupRef, setGrupRef] = useState<string | null>(null);
  const anaZorunlu = tanim.eklemeSarti === 'ana_kalem';
  const grupZorunlu = tanim.eklemeSarti === 'grup';
  const dokunuldu = ad !== '' || anaKalemId !== null || grupRef !== null;
  // P12 (madde 224) — kaydedilmemiş sözlük kalemi çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'sözlük kalemi');
  const kaydedilebilir =
    ad.trim() !== '' && (!anaZorunlu || anaKalemId !== null) && (!grupZorunlu || grupRef !== null);

  return (
    <SolukSatir
      testId="sozluk-kalem-soluk-satir"
      dokunuldu={dokunuldu}
      kaydedilebilir={kaydedilebilir}
      kaydediliyor={kaydediliyor}
      kolonSayisi={kolonSayisi}
      onKaydet={() => {
        if (!kaydedilebilir) return;
        onKaydet({
          ad: ad.trim(),
          anaKalemId,
          kurallar: grupRef ? { grup_ref: grupRef } : undefined,
        });
      }}
    >
      <Table.Td>
        <TextInput
          size="xs"
          aria-label="Kalem adı"
          placeholder="Kalem adı"
          data-testid="sozluk-kalem-ad"
          value={ad}
          onChange={(e) => setAd(e.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td colSpan={kolonSayisi - 2}>
        {/* Bağ, listenin sınıfına göre sorulur (karışık liste): ana kalem
            zorunluysa boş bırakılamaz; kütüphaneli listede isteğe bağlıdır. */}
        {(anaZorunlu || (tanim.sinif === 'kutuphane' && anaKalemler.length > 0)) && (
          <Select
            size="xs"
            searchable
            clearable={!anaZorunlu}
            comboboxProps={{ withinPortal: true }}
            aria-label="Ana kalem"
            placeholder={anaZorunlu ? 'Ana kalem (zorunlu)' : 'Ana kalem (isteğe bağlı)'}
            data-testid="sozluk-kalem-ana"
            data={anaKalemler.map((k) => ({ value: k.id, label: k.ad }))}
            value={anaKalemId}
            onChange={setAnaKalemId}
          />
        )}
        {grupZorunlu && (
          <Select
            size="xs"
            searchable
            comboboxProps={{ withinPortal: true }}
            aria-label="Grup"
            placeholder="Grup (zorunlu)"
            data-testid="sozluk-kalem-grup"
            data={grupSecenekleri}
            value={grupRef}
            onChange={setGrupRef}
          />
        )}
      </Table.Td>
      <Table.Td>
        <Button
          size="compact-xs"
          variant="subtle"
          onClick={onVazgec}
          data-testid="sozluk-kalem-vazgec"
        >
          Vazgeç
        </Button>
      </Table.Td>
    </SolukSatir>
  );
}

// ── Kural özeti (okuma hücresi) ─────────────────────────────────────────────

const SURE_SINIFI_ETIKET: Record<SureSinifi, string> = {
  kural: 'Sistem hesaplar (tarih + periyot)',
  belge: 'Belgenin üstünde (kayıtta girilir)',
  suresiz: 'Süresiz',
};
const EKSEN_ETIKET: Record<GorevlendirmeNesneEkseni, string> = {
  yok: 'İşyeri düzeyi',
  ekipman_tipi: 'Ekipman tipine bağlı',
};

function periyotMetni(p: { az: number; tehlikeli: number; cok: number } | null): string | null {
  return p ? `${p.az} / ${p.tehlikeli} / ${p.cok}` : null;
}

function zorunluSayiMetni(z: ZorunluSayi | null): string {
  if (z === null) return 'Zorunlu sayı yok';
  if (z.bicim === 'oran') {
    return z.bolen === null
      ? 'Oran — sayı bekliyor'
      : `Oran: ${z.bolen.az} / ${z.bolen.tehlikeli} / ${z.bolen.cok} çalışana 1`;
  }
  return `Basamak: ${z.basamaklar.map((b) => `${b.alt}-${b.ust ?? '∞'}→${b.sayi}`).join(' · ')}`;
}

function KuralOzeti({
  editor,
  kalem,
  sartAdi,
}: {
  editor: NonNullable<SozlukKuralEditoru>;
  kalem: Kalem;
  sartAdi: ReadonlyMap<string, string>;
}): React.JSX.Element {
  switch (editor) {
    case 'sure_sinifi': {
      const sinif = readSureSinifi(kalem.kurallar);
      const periyot = periyotMetni(readGecerlilikAy(kalem.kurallar));
      return (
        <Group gap="xs">
          {sinif ? (
            <Rozet anlam="bilgi" testId="egitim-turu-sinif">
              {SURE_SINIFI_ETIKET[sinif]}
            </Rozet>
          ) : (
            <Text size="xs" c="dimmed">
              Tanımsız
            </Text>
          )}
          <Text size="xs" data-testid="egitim-turu-periyot">
            {periyot ?? (sinif === 'kural' ? 'Tanımlanmamış — yetkinlik süresiz sayılır' : '—')}
          </Text>
        </Group>
      );
    }
    case 'bakanlik_kodu':
    case 'muayene': {
      const kod = readBakanlikKodu(kalem.kurallar);
      const periyot =
        editor === 'muayene' ? periyotMetni(readMuayenePeriyodu(kalem.kurallar)) : null;
      return (
        <Group gap="xs">
          <Text size="sm" data-testid="bakanlik-kodu-degeri">
            {kod ?? 'Doğrulanacak — boş'}
          </Text>
          {editor === 'muayene' && (
            <Text size="xs" c="dimmed" data-testid="muayene-periyot-degeri">
              {periyot ?? 'periyot —'}
            </Text>
          )}
        </Group>
      );
    }
    case 'uyari_esigi': {
      const gun = readUyariEsigiGun(kalem.kurallar);
      return (
        <Group gap="xs">
          <Text size="sm" data-testid="uyari-esigi-degeri">
            {gun === null ? 'Varsayılan (30)' : `${gun} gün`}
          </Text>
          {/* ACİL DURUM / E3 (Kapsam §29): tipin işareti — ikinci envanter yok. */}
          {kalem.listeKodu === 'equipment_tip' && readAcilDurumEkipmaniMi(kalem.kurallar) && (
            <Rozet anlam="bilgi" testId="acil-durum-ekipmani-rozeti">
              Acil durum ekipmanı
            </Rozet>
          )}
        </Group>
      );
    }
    case 'evrak': {
      // TUR 6 / Ş6 — evrak tipi kuralı (kütüphane başlangıç; müşteri değiştirir).
      const kural = readEvrakTipiKurali(kalem.kurallar);
      return (
        <Group gap="xs" data-testid="evrak-tipi-kurali">
          <Rozet anlam="bilgi">{EVRAK_EKSENI_ETIKETI[kural.eksen]}</Rozet>
          <Text size="xs">{kural.vadeTakibi ? 'vadeli' : 'vadesiz'}</Text>
          {kural.saglik && (
            <Rozet anlam="dikkat" testId="evrak-tipi-saglik">
              Sağlık tipi
            </Rozet>
          )}
          <Text size="xs" c="dimmed">
            eşik {kural.uyariEsigiGun === null ? 'varsayılan (30)' : `${kural.uyariEsigiGun} gün`}
          </Text>
        </Group>
      );
    }
    case 'gorevlendirme': {
      const kural = readGorevlendirmeKurali(kalem.kurallar);
      return (
        <Stack gap={4}>
          <Group gap="xs">
            <Text size="xs">{EKSEN_ETIKET[kural.nesneEkseni]}</Text>
            {kural.saglikKanaatiGerekli ? (
              <Rozet anlam="bilgi" testId="gorevlendirme-turu-saglik">
                Sağlık aranıyor
              </Rozet>
            ) : (
              <Text size="xs" c="dimmed">
                Sağlık aranmıyor
              </Text>
            )}
          </Group>
          {kural.gerekenYetkinlikler.length === 0 ? (
            <Text size="xs" c="dimmed">
              Belge aranmıyor
            </Text>
          ) : (
            <Group gap={4}>
              {kural.gerekenYetkinlikler.map((id) => (
                // Çözülemeyen kalem GİZLENMEZ, SÖYLENİR (sessiz uygunluk hatası).
                <Rozet key={id} anlam={sartAdi.has(id) ? 'bilgi' : 'dikkat'}>
                  {sartAdi.get(id) ?? 'Kaldırılmış kalem'}
                </Rozet>
              ))}
            </Group>
          )}
          <Text size="xs" c="dimmed" data-testid="gorevlendirme-turu-zorunlu">
            {zorunluSayiMetni(kural.zorunluSayi)}
          </Text>
        </Stack>
      );
    }
  }
}

// ── Kural düzenleme satırı (aynı anda TEK satır — P1-c) ─────────────────────

function KalemDuzenleSatiri({
  kalem,
  editor,
  sartSecenekleri,
  kaydediliyor,
  onKaydet,
  onVazgec,
}: {
  kalem: Kalem;
  editor: SozlukKuralEditoru;
  sartSecenekleri: { value: string; label: string }[];
  kaydediliyor: boolean;
  /** `kurallar` null = kural değişmedi; `ad` null = ad değişmedi. */
  onKaydet: (kurallar: Record<string, unknown> | null, ad: string | null) => void;
  onVazgec: () => void;
}): React.JSX.Element {
  const musteriKalemi = kalem.kutuphaneRef === null;
  const [ad, setAd] = useState(kalem.ad);
  const mevcut = (kalem.kurallar ?? {}) as Record<string, unknown>;

  // Süre sınıfı
  const [sinif, setSinif] = useState<SureSinifi>(readSureSinifi(kalem.kurallar) ?? 'kural');
  const p0 = readGecerlilikAy(kalem.kurallar) ?? readMuayenePeriyodu(kalem.kurallar);
  const [az, setAz] = useState<number | string>(p0?.az ?? '');
  const [tehlikeli, setTehlikeli] = useState<number | string>(p0?.tehlikeli ?? '');
  const [cok, setCok] = useState<number | string>(p0?.cok ?? '');
  // Bakanlık kodu
  const [kod, setKod] = useState(readBakanlikKodu(kalem.kurallar) ?? '');
  // Uyarı eşiği
  const [esik, setEsik] = useState<number | string>(readUyariEsigiGun(kalem.kurallar) ?? '');
  // ACİL DURUM / E3 — ekipman tipinin işareti (yalnız `equipment_tip`).
  const [acilDurum, setAcilDurum] = useState(readAcilDurumEkipmaniMi(kalem.kurallar));
  // Görevlendirme
  const g0 = readGorevlendirmeKurali(kalem.kurallar);
  const [eksen, setEksen] = useState<GorevlendirmeNesneEkseni>(g0.nesneEkseni);
  const [sartlar, setSartlar] = useState<string[]>(g0.gerekenYetkinlikler);
  const [saglik, setSaglik] = useState(g0.saglikKanaatiGerekli);
  const [zorunlu, setZorunlu] = useState<ZorunluSayi | null>(g0.zorunluSayi);
  // Evrak tipi (TUR 6 / Ş6)
  const e0 = readEvrakTipiKurali(kalem.kurallar);
  const [evrakEksen, setEvrakEksen] = useState<(typeof EVRAK_EKSENLERI)[number]>(e0.eksen);
  const [vadeTakibi, setVadeTakibi] = useState(e0.vadeTakibi);
  const [evrakSaglik, setEvrakSaglik] = useState(e0.saglik);

  const periyotDolu = az !== '' && tehlikeli !== '' && cok !== '';
  const periyotYarim = !periyotDolu && (az !== '' || tehlikeli !== '' || cok !== '');
  const periyotGovdesi = () =>
    periyotDolu
      ? { gecerlilik_ay: { az: Number(az), tehlikeli: Number(tehlikeli), cok: Number(cok) } }
      : {};

  function kurallariKur(): Record<string, unknown> | null {
    // ⚠️ BİLİNMEYEN ANAHTARLAR KORUNUR — mevcut gövde üstüne yazılır.
    switch (editor) {
      case 'sure_sinifi': {
        const { gecerlilik_ay: _eski, ...kalan } = mevcut;
        void _eski;
        return { ...kalan, sure_sinifi: sinif, ...periyotGovdesi() };
      }
      case 'bakanlik_kodu':
        return { ...mevcut, bakanlik_kodu: kod.trim() === '' ? null : kod.trim() };
      case 'muayene': {
        const { gecerlilik_ay: _eski, ...kalan } = mevcut;
        void _eski;
        return {
          ...kalan,
          bakanlik_kodu: kod.trim() === '' ? null : kod.trim(),
          ...periyotGovdesi(),
        };
      }
      case 'uyari_esigi': {
        const { uyari_esigi_gun: _eski, ...kalan } = mevcut;
        void _eski;
        return {
          ...kalan,
          ...(esik === '' ? {} : { uyari_esigi_gun: Number(esik) }),
          // Yalnız ekipman tipinde anlamlı; diğer listelerde anahtar YAZILMAZ.
          ...(kalem.listeKodu === 'equipment_tip' ? { acil_durum_ekipmani: acilDurum } : {}),
        };
      }
      case 'gorevlendirme': {
        const { zorunlu_sayi: _eski, ...kalan } = mevcut;
        void _eski;
        return {
          ...kalan,
          nesne_ekseni: eksen,
          gereken_yetkinlikler: sartlar,
          saglik_kanaati_gerekli: saglik,
          ...(zorunlu ? { zorunlu_sayi: zorunlu } : {}),
        };
      }
      case 'evrak': {
        // ✏️ K-102i (17.09.2026): `zorunlu` YAZILMAZ — zorunluluk yüklenicinin
        // evrak paketindedir; eski gövdede kalmışsa da düşürülür (sunucu 422'ler).
        const { uyari_esigi_gun: _eski, zorunlu: _zorunlu, ...kalan } = mevcut;
        void _eski;
        void _zorunlu;
        return {
          ...kalan,
          eksen: evrakEksen,
          vade_takibi: vadeTakibi,
          saglik: evrakSaglik,
          ...(esik === '' ? {} : { uyari_esigi_gun: Number(esik) }),
        };
      }
      default:
        return null;
    }
  }

  const periyotUyari = editor === 'sure_sinifi' || editor === 'muayene';
  const kaydedilebilir = ad.trim() !== '' && !(periyotUyari && periyotYarim);

  return (
    <Table.Tr data-testid="sozluk-kalem-duzenleme">
      <Table.Td>
        {musteriKalemi ? (
          <TextInput
            size="xs"
            aria-label="Kalem adı"
            data-testid="sozluk-kalem-ad-duzelt"
            value={ad}
            onChange={(e) => setAd(e.currentTarget.value)}
          />
        ) : (
          // Nüshanın adı üründen gelir — kilitli (§19/9).
          kalem.ad
        )}
      </Table.Td>
      <Table.Td>
        <Rozet anlam="bilgi">{musteriKalemi ? 'Müşteri' : 'Kütüphane'}</Rozet>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          {kalem.gizli ? 'Gizli' : '—'}
        </Text>
      </Table.Td>
      {editor && (
        <Table.Td>
          {editor === 'sure_sinifi' && (
            <Group gap={4} wrap="nowrap">
              <Select
                size="xs"
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
                aria-label="Süre türü"
                data-testid="egitim-turu-sinif-secim"
                value={sinif}
                onChange={(d) => setSinif((d ?? 'kural') as SureSinifi)}
                data={SURE_SINIFLARI.map((s) => ({ value: s, label: SURE_SINIFI_ETIKET[s] }))}
              />
              <PeriyotGirisi
                az={az}
                tehlikeli={tehlikeli}
                cok={cok}
                setAz={setAz}
                setTehlikeli={setTehlikeli}
                setCok={setCok}
                onek="egitim-turu-periyot"
              />
            </Group>
          )}
          {(editor === 'bakanlik_kodu' || editor === 'muayene') && (
            <Group gap={4} wrap="nowrap">
              <TextInput
                size="xs"
                aria-label="Bakanlık kodu"
                data-testid="bakanlik-kodu-giris"
                placeholder="doğrulanmış kod"
                value={kod}
                onChange={(e) => setKod(e.currentTarget.value)}
              />
              {editor === 'muayene' && (
                <PeriyotGirisi
                  az={az}
                  tehlikeli={tehlikeli}
                  cok={cok}
                  setAz={setAz}
                  setTehlikeli={setTehlikeli}
                  setCok={setCok}
                  onek="muayene-periyot"
                />
              )}
            </Group>
          )}
          {editor === 'uyari_esigi' && (
            <Group gap="xs" wrap="nowrap">
              <NumberInput
                size="xs"
                min={1}
                w={120}
                aria-label="Uyarı eşiği (gün)"
                data-testid="uyari-esigi-giris"
                placeholder="30"
                value={esik}
                onChange={setEsik}
              />
              {kalem.listeKodu === 'equipment_tip' && (
                <Checkbox
                  size="xs"
                  label="Acil durum ekipmanı"
                  data-testid="acil-durum-ekipmani-giris"
                  checked={acilDurum}
                  onChange={(e) => {
                    // D1: değer güncelleyiciden ÖNCE okunur.
                    const deger = e.currentTarget.checked;
                    setAcilDurum(deger);
                  }}
                />
              )}
            </Group>
          )}
          {editor === 'evrak' && (
            <Group gap={4} wrap="nowrap">
              <Select
                size="xs"
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
                aria-label="Evrak ekseni"
                data-testid="evrak-tipi-eksen"
                value={evrakEksen}
                onChange={(d) => setEvrakEksen((d ?? 'firma') as (typeof EVRAK_EKSENLERI)[number])}
                data={EVRAK_EKSENLERI.map((e) => ({ value: e, label: EVRAK_EKSENI_ETIKETI[e] }))}
              />
              <Select
                size="xs"
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
                aria-label="Vade takibi"
                data-testid="evrak-tipi-vade"
                value={vadeTakibi ? 'vadeli' : 'vadesiz'}
                onChange={(d) => setVadeTakibi(d === 'vadeli')}
                data={[
                  { value: 'vadeli', label: 'Vadeli' },
                  { value: 'vadesiz', label: 'Vadesiz' },
                ]}
              />
              <Select
                size="xs"
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
                aria-label="Sağlık tipi"
                data-testid="evrak-tipi-saglik-secim"
                value={evrakSaglik ? 'evet' : 'hayir'}
                onChange={(d) => setEvrakSaglik(d === 'evet')}
                data={[
                  { value: 'hayir', label: 'Sağlık değil' },
                  { value: 'evet', label: 'Sağlık tipi' },
                ]}
              />
              <NumberInput
                size="xs"
                min={1}
                w={110}
                aria-label="Uyarı eşiği (gün)"
                data-testid="evrak-tipi-esik"
                placeholder="30"
                value={esik}
                onChange={setEsik}
              />
            </Group>
          )}
          {editor === 'gorevlendirme' && (
            <Stack gap={4}>
              <Group gap={4} wrap="nowrap">
                <Select
                  size="xs"
                  allowDeselect={false}
                  comboboxProps={{ withinPortal: true }}
                  aria-label="Nesne ekseni"
                  data-testid="gorevlendirme-turu-eksen"
                  value={eksen}
                  onChange={(d) => setEksen((d ?? 'yok') as GorevlendirmeNesneEkseni)}
                  data={GOREVLENDIRME_NESNE_EKSENLERI.map((e) => ({
                    value: e,
                    label: EKSEN_ETIKET[e],
                  }))}
                />
                <Select
                  size="xs"
                  allowDeselect={false}
                  comboboxProps={{ withinPortal: true }}
                  aria-label="Sağlık kanaati"
                  data-testid="gorevlendirme-turu-saglik-secim"
                  value={saglik ? 'evet' : 'hayir'}
                  onChange={(d) => setSaglik(d === 'evet')}
                  data={[
                    { value: 'hayir', label: 'Sağlık aranmıyor' },
                    { value: 'evet', label: 'Sağlık aranıyor' },
                  ]}
                />
              </Group>
              <MultiSelect
                size="xs"
                searchable
                clearable
                comboboxProps={{ withinPortal: true }}
                placeholder="Belge / eğitim seç"
                aria-label="Gerekli yetkinlikler"
                data-testid="gorevlendirme-turu-sartlar"
                value={sartlar}
                onChange={setSartlar}
                data={sartSecenekleri}
              />
              <ZorunluSayiGirisi deger={zorunlu} onChange={setZorunlu} />
            </Stack>
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <Button
            size="compact-xs"
            loading={kaydediliyor}
            disabled={!kaydedilebilir}
            data-testid="sozluk-kalem-kaydet"
            onClick={() => onKaydet(kurallariKur(), musteriKalemi ? ad.trim() : null)}
          >
            Kaydet
          </Button>
          <Button
            size="compact-xs"
            variant="subtle"
            onClick={onVazgec}
            data-testid="sozluk-kalem-vazgec"
          >
            Vazgeç
          </Button>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function PeriyotGirisi({
  az,
  tehlikeli,
  cok,
  setAz,
  setTehlikeli,
  setCok,
  onek,
}: {
  az: number | string;
  tehlikeli: number | string;
  cok: number | string;
  setAz: (v: number | string) => void;
  setTehlikeli: (v: number | string) => void;
  setCok: (v: number | string) => void;
  onek: string;
}): React.JSX.Element {
  return (
    <Group gap={4} wrap="nowrap">
      <NumberInput
        size="xs"
        min={1}
        w={80}
        aria-label="Az tehlikeli (ay)"
        data-testid={`${onek}-az`}
        value={az}
        onChange={setAz}
        placeholder="az"
      />
      <NumberInput
        size="xs"
        min={1}
        w={80}
        aria-label="Tehlikeli (ay)"
        data-testid={`${onek}-tehlikeli`}
        value={tehlikeli}
        onChange={setTehlikeli}
        placeholder="tehlikeli"
      />
      <NumberInput
        size="xs"
        min={1}
        w={80}
        aria-label="Çok tehlikeli (ay)"
        data-testid={`${onek}-cok`}
        value={cok}
        onChange={setCok}
        placeholder="çok"
      />
    </Group>
  );
}

/**
 * 5½ / B2 — ZORUNLU SAYI editörü: iki biçim (basamak · oran). Kütüphane
 * tabanının altına inen yazım sunucuda 422 (sert kural — band gösterir);
 * ekran ikinci kural yazmaz.
 */
function ZorunluSayiGirisi({
  deger,
  onChange,
}: {
  deger: ZorunluSayi | null;
  onChange: (z: ZorunluSayi | null) => void;
}): React.JSX.Element {
  const bicim = deger?.bicim ?? 'yok';
  return (
    <Stack gap={4} data-testid="zorunlu-sayi-editor">
      <Select
        size="xs"
        allowDeselect={false}
        comboboxProps={{ withinPortal: true }}
        aria-label="Zorunlu sayı biçimi"
        data-testid="zorunlu-sayi-bicim"
        value={bicim}
        onChange={(d) => {
          if (d === 'basamak')
            onChange({ bicim: 'basamak', basamaklar: [{ alt: 1, ust: null, sayi: 1 }] });
          else if (d === 'oran') onChange({ bicim: 'oran', bolen: null });
          else onChange(null);
        }}
        data={[
          { value: 'yok', label: 'Zorunlu sayı yok' },
          { value: 'basamak', label: 'Basamak tablosu (çalışan aralığı → sayı)' },
          { value: 'oran', label: 'Oran (N çalışana 1)' },
        ]}
      />
      {deger?.bicim === 'oran' && (
        <Group gap={4} wrap="nowrap">
          {(['az', 'tehlikeli', 'cok'] as const).map((k) => (
            <NumberInput
              key={k}
              size="xs"
              min={1}
              w={90}
              aria-label={`Bölen ${k}`}
              data-testid={`zorunlu-sayi-bolen-${k}`}
              placeholder={k}
              value={deger.bolen?.[k] ?? ''}
              onChange={(v) => {
                const b = deger.bolen ?? { az: 0, tehlikeli: 0, cok: 0 };
                const yeni = { ...b, [k]: Number(v) || 0 };
                onChange({
                  bicim: 'oran',
                  bolen: yeni.az > 0 && yeni.tehlikeli > 0 && yeni.cok > 0 ? yeni : null,
                });
              }}
            />
          ))}
        </Group>
      )}
      {deger?.bicim === 'basamak' && (
        <Stack gap={2}>
          {deger.basamaklar.map((b, i) => (
            <Group key={i} gap={4} wrap="nowrap" data-testid="zorunlu-sayi-basamak">
              <NumberInput
                size="xs"
                min={0}
                w={80}
                aria-label="Alt sınır"
                data-testid={`zorunlu-sayi-alt-${i}`}
                value={b.alt}
                onChange={(v) =>
                  onChange({
                    bicim: 'basamak',
                    basamaklar: deger.basamaklar.map((x, j) =>
                      j === i ? { ...x, alt: Number(v) || 0 } : x,
                    ),
                  })
                }
              />
              <NumberInput
                size="xs"
                min={0}
                w={80}
                aria-label="Üst sınır"
                data-testid={`zorunlu-sayi-ust-${i}`}
                placeholder="∞"
                value={b.ust ?? ''}
                onChange={(v) =>
                  onChange({
                    bicim: 'basamak',
                    basamaklar: deger.basamaklar.map((x, j) =>
                      j === i ? { ...x, ust: v === '' ? null : Number(v) } : x,
                    ),
                  })
                }
              />
              <NumberInput
                size="xs"
                min={0}
                w={70}
                aria-label="Sayı"
                data-testid={`zorunlu-sayi-sayi-${i}`}
                value={b.sayi}
                onChange={(v) =>
                  onChange({
                    bicim: 'basamak',
                    basamaklar: deger.basamaklar.map((x, j) =>
                      j === i ? { ...x, sayi: Number(v) || 0 } : x,
                    ),
                  })
                }
              />
              <ActionIcon
                size="sm"
                variant="subtle"
                aria-label="Basamağı sil"
                disabled={deger.basamaklar.length === 1}
                onClick={() =>
                  onChange({
                    bicim: 'basamak',
                    basamaklar: deger.basamaklar.filter((_, j) => j !== i),
                  })
                }
              >
                ×
              </ActionIcon>
            </Group>
          ))}
          <Button
            size="compact-xs"
            variant="subtle"
            data-testid="zorunlu-sayi-basamak-ekle"
            onClick={() =>
              onChange({
                bicim: 'basamak',
                basamaklar: [
                  ...deger.basamaklar,
                  { alt: (deger.basamaklar.at(-1)?.ust ?? 0) + 1, ust: null, sayi: 1 },
                ],
              })
            }
          >
            + Basamak
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// "DİĞER" KUYRUĞU — T3; kapısı AYNI uç (`olay_inceleme` / `risk_degerlendirme`)
// ═══════════════════════════════════════════════════════════════════════════

function DigerKuyruguBolumu({
  listeKodu,
}: {
  listeKodu: LookupListeKodu | null;
}): React.JSX.Element {
  const kuyruk = useDigerKuyrugu();
  const options = useOlayOptions();
  const [acik, setAcik] = useState<string | null>(null);
  const satirlar = (kuyruk.data ?? []).filter(
    (s) => listeKodu === null || s.listeKodu === listeKodu,
  );
  const anahtar = (s: KuyrukSatiri) => `${s.listeKodu}-${s.metin}`;
  const ilkKapaliSatir = satirlar.find((s) => !s.kalemKapisi.yazabilir);

  return (
    <Stack data-testid="diger-kuyrugu-bolumu">
      <Title order={listeKodu === null ? 4 : 5}>“Diğer” kuyruğu</Title>
      <QueryStatus queries={[kuyruk]} />
      {/* P13 — dönüştüremeyen kullanıcı sebebi bölümde BİR KEZ görür. */}
      {ilkKapaliSatir && (
        <KapiSebebi
          durum={ilkKapaliSatir.kalemKapisi}
          nesne="Serbest metni kalıcı kaleme"
          fiil="dönüştürür"
          testId="diger-kuyrugu-yazamama-sebebi"
        />
      )}
      {kuyruk.isSuccess && satirlar.length === 0 && (
        <BosListeNotu mesaj={'Kuyruk boş — "Diğer" ile girilmiş serbest metin yok.'} />
      )}
      {kuyruk.isSuccess && satirlar.length > 0 && (
        <Table striped data-testid="diger-kuyrugu-tablo">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Kaynak</Table.Th>
              <Table.Th>Sözlük</Table.Th>
              <Table.Th>Serbest metin</Table.Th>
              <Table.Th>Sıklık</Table.Th>
              <Table.Th>Kayıtlar</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {satirlar.map((satir) => (
              <KuyrukSatirlari
                key={anahtar(satir)}
                satir={satir}
                acik={acik === anahtar(satir)}
                onAc={() => setAcik(anahtar(satir))}
                onKapat={() => setAcik(null)}
                anaKalemler={(options.data?.olaySiniflari ?? []).filter((s) => !s.digerMi)}
                gruplar={options.data?.kokSebepGruplari ?? []}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function KuyrukSatirlari({
  satir,
  acik,
  onAc,
  onKapat,
  anaKalemler,
  gruplar,
}: {
  satir: KuyrukSatiri;
  acik: boolean;
  onAc: () => void;
  onKapat: () => void;
  anaKalemler: { id: string; ad: string }[];
  gruplar: { ref: string; ad: string }[];
}): React.JSX.Element {
  const ekle = useDigerKalemEkle();
  const [ad, setAd] = useState(satir.metin);
  const [anaKalemId, setAnaKalemId] = useState<string | null>(null);
  const [grupRef, setGrupRef] = useState<string | null>(null);
  const navigate = useNavigate();
  return (
    <>
      <Table.Tr data-testid="diger-kuyrugu-satir">
        <Table.Td>
          <Badge color={ANLAM.bilgi} variant="filled">
            {KAYNAK_ETIKET[satir.kaynak] ?? satir.kaynak}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge variant="light">{SOZLUK_LISTE_ETIKET[satir.listeKodu]}</Badge>
        </Table.Td>
        <Table.Td>{satir.metin}</Table.Td>
        <Table.Td>{satir.sayi}</Table.Td>
        <Table.Td>
          <Group gap="xs">
            {satir.ornekler.map((ornek) => (
              <Text
                key={ornek.kayitId}
                size="xs"
                component="a"
                href={(KAYNAK_YOLU[satir.kaynak] ?? KAYNAK_YOLU.olay!)(ornek.kayitId)}
                c={ANLAM.bilgi}
                onClick={(olay) => {
                  olay.preventDefault();
                  void navigate((KAYNAK_YOLU[satir.kaynak] ?? KAYNAK_YOLU.olay!)(ornek.kayitId));
                }}
              >
                {ornek.etiket}
              </Text>
            ))}
          </Group>
        </Table.Td>
        <Table.Td>
          {/* Kuyruğu OKUYAN herkes dönüştüremez (holding yön. · izleyici okur):
              düğme satırın `kalemKapisi`nden çizilir; sebep bölümde bir kez. */}
          {satir.kalemKapisi.yazabilir && !acik && !ekle.isSuccess && (
            <Button
              size="compact-xs"
              variant="light"
              data-testid="diger-kaleme-donustur"
              onClick={onAc}
            >
              Kaleme dönüştür
            </Button>
          )}
          {ekle.isSuccess && (
            <Text size="xs" c={ANLAM.olumlu} data-testid="diger-kalem-eklendi">
              Kalem eklendi: {ekle.data.ad}
            </Text>
          )}
        </Table.Td>
      </Table.Tr>
      {/* P1-c — dönüştürme SATIR İÇİ; modal yalnız onay sorularına ayrılmıştır. */}
      {acik && !ekle.isSuccess && (
        <Table.Tr data-testid="diger-kalem-formu">
          <Table.Td colSpan={6}>
            <Group gap="xs" align="flex-end" wrap="wrap">
              <TextInput
                size="xs"
                label="Kalem adı"
                required
                data-testid="diger-kalem-ad"
                value={ad}
                onChange={(e) => setAd(e.currentTarget.value)}
              />
              {satir.listeKodu === 'olay_sinifi' && (
                <Select
                  size="xs"
                  label="Ana kalem"
                  comboboxProps={{ withinPortal: true }}
                  data-testid="diger-kalem-ana"
                  data={anaKalemler.map((k) => ({ value: k.id, label: k.ad }))}
                  value={anaKalemId}
                  onChange={setAnaKalemId}
                />
              )}
              {satir.listeKodu === 'kok_sebep' && (
                <Select
                  size="xs"
                  label="Grup"
                  comboboxProps={{ withinPortal: true }}
                  data-testid="diger-kalem-grup"
                  data={gruplar.map((g) => ({ value: g.ref, label: g.ad }))}
                  value={grupRef}
                  onChange={setGrupRef}
                />
              )}
              <Button
                size="xs"
                data-testid="diger-kalem-kaydet"
                disabled={ad.trim() === ''}
                loading={ekle.isPending}
                onClick={() =>
                  ekle.mutate({ listeKodu: satir.listeKodu, ad: ad.trim(), anaKalemId, grupRef })
                }
              >
                Kalem ekle
              </Button>
              <Button size="xs" variant="subtle" onClick={onKapat}>
                Vazgeç
              </Button>
              {ekle.isError && (
                <Text size="sm" c={ANLAM.engel} role="alert" data-testid="diger-kalem-hata">
                  {ekle.error.message}
                </Text>
              )}
            </Group>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}
